/*
 * Shared pipe machinery: adapts a postMessage endpoint (a dedicated Worker, a SharedWorker
 * port) into a WorkerFramePipe. Inbound data is decoded through the zod envelope schema — the
 * one frame-parsing mechanism — and malformed data is dropped loudly.
 */
import { safeParseWorkerFrame } from '../envelope';
import type { WorkerFrame } from '../envelope';
import type { WorkerFramePipe } from './types';

export interface PipeEndpoint {
    postMessage(data: unknown): void;
    /** The object emitting 'message' events (a Worker, or a SharedWorker's port). */
    readonly messageTarget: EventTarget;
    /** The object emitting 'error' events (the Worker/SharedWorker itself), if any. */
    readonly errorTarget?: EventTarget;
    /** Release the underlying resource: terminate the worker / close the port. */
    teardown(): void;
}

export class EndpointFramePipe implements WorkerFramePipe {
    readonly #stack = new DisposableStack();
    readonly #teardown = new AbortController();
    readonly #endpoint: PipeEndpoint;
    readonly #frameHandlers = new Set<(frame: WorkerFrame) => void>();
    readonly #closeHandlers = new Set<(error?: unknown) => void>();
    /** onClose fires once; latched independently of disposal. */
    #closeEmitted = false;

    constructor(endpoint: PipeEndpoint) {
        this.#endpoint = endpoint;
        // Handlers register against the teardown signal, so intentional disposal detaches them
        // before the closure it causes could be observed as a failure.
        endpoint.messageTarget.addEventListener(
            'message',
            (event) => {
                const frame = safeParseWorkerFrame((event as MessageEvent).data);
                if (frame === undefined) {
                    // eslint-disable-next-line no-console -- protocol violation must be loud; pipes predate any Logger
                    console.error('[worker-pipe] dropped malformed inbound frame');
                    return;
                }
                for (const handler of [...this.#frameHandlers]) {
                    handler(frame);
                }
            },
            { signal: this.#teardown.signal },
        );
        endpoint.errorTarget?.addEventListener(
            'error',
            (event) => {
                this.#emitClose('error' in event ? event.error : event);
            },
            { signal: this.#teardown.signal },
        );
        this.#stack.defer(() => endpoint.teardown());
    }

    #emitClose(error?: unknown): void {
        if (this.#closeEmitted) {
            return;
        }
        this.#closeEmitted = true;
        for (const handler of [...this.#closeHandlers]) {
            handler(error);
        }
    }

    send(frame: WorkerFrame): void {
        this.#endpoint.postMessage(frame);
    }

    onFrame(handler: (frame: WorkerFrame) => void): Disposable {
        this.#frameHandlers.add(handler);
        return {
            [Symbol.dispose]: () => {
                this.#frameHandlers.delete(handler);
            },
        };
    }

    onClose(handler: (error?: unknown) => void): Disposable {
        this.#closeHandlers.add(handler);
        return {
            [Symbol.dispose]: () => {
                this.#closeHandlers.delete(handler);
            },
        };
    }

    [Symbol.dispose](): void {
        if (this.#stack.disposed) {
            return;
        }
        this.#teardown.abort();
        this.#frameHandlers.clear();
        this.#closeHandlers.clear();
        this.#stack.dispose();
    }
}
